import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Images } from 'lucide-react';
import { projects } from '../data/mock';
import { Button } from '../components/ui/button';
import MediaGallery from '../components/MediaGallery';

const categories = ['All', 'Full Renovations', 'Flooring', 'Wallpaper', 'Decking', 'Custom Projects'];

const ProjectsPage = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [selectedProject, setSelectedProject] = useState(null);

  const filteredProjects = activeCategory === 'All'
    ? projects
    : projects.filter((p) => p.category === activeCategory);

  const openGallery = (project) => {
    setSelectedProject(project);
  };

  const closeGallery = () => {
    setSelectedProject(null);
  };

  return (
    <div className="bg-white pt-16 lg:pt-20">
      {/* Hero Section - Dark */}
      <section className="relative py-20 lg:py-28 bg-zinc-900 overflow-hidden">
        {/* Background */}
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{
            // backgroundImage: `url('https://images.pexels.com/photos/8820168/pexels-photo-8820168.jpeg?w=1920')`,
            backgroundImage: `url('/images/Full Renovations/1.0.jpg')`,
          }}
        />

        {/* Orange accent */}
        <div className="absolute left-0 top-0 bottom-0 w-1 lg:w-2 bg-orange-500" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 lg:px-6">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-12 h-0.5 bg-orange-500" />
              <span className="text-orange-500 text-xs font-bold uppercase tracking-widest">Our Work</span>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-white uppercase tracking-tight leading-[0.9] mb-6">
              Projects
            </h1>
            <p className="text-xl text-zinc-300 leading-relaxed">
              Real work. Real results. Click any project to view the full gallery.
            </p>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-6 border-b border-zinc-200 sticky top-16 lg:top-20 bg-white z-40">
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-5 py-2.5 text-xs font-black uppercase tracking-wide transition-all duration-200 border ${
                  activeCategory === category
                    ? 'bg-orange-500 border-orange-500 text-white'
                    : 'bg-white border-zinc-300 text-zinc-600 hover:border-orange-500 hover:text-orange-500'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid - Light */}
      <section className="py-12 lg:py-16 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map((project) => (
              <div
                key={project.id}
                onClick={() => openGallery(project)}
                className="group cursor-pointer bg-white shadow-sm hover:shadow-lg transition-shadow overflow-hidden"
              >
                {/* Cover Image */}
                <div className="aspect-[4/3] overflow-hidden relative">
                  <img
                    src={project.coverImage}
                    alt={project.title}
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
                  />
                  
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-orange-500 text-white font-black uppercase text-xs px-4 py-2 flex items-center gap-2">
                      <Images className="w-4 h-4" />
                      View Gallery
                    </div>
                  </div>

                  {/* Category badge */}
                  <div className="absolute top-4 left-4">
                    <span className="inline-block px-3 py-1 bg-orange-500 text-white text-xs font-black uppercase tracking-wide">
                      {project.category}
                    </span>
                  </div>

                  {/* Media count badge */}
                  <div className="absolute top-4 right-4 bg-black/60 px-2 py-1 flex items-center gap-1.5 text-white text-xs font-bold">
                    <Images className="w-3.5 h-3.5" />
                    {project.media.length}
                  </div>
                </div>

                {/* Project Info - Caption below thumbnail */}
                <div className="p-5">
                  <h3 className="text-lg font-black text-zinc-900 tracking-tight mb-2">
                    {project.title}
                  </h3>
                  {/* Feel free to enable the description under each project. */}
                  {/* <p className="text-zinc-600 text-sm leading-relaxed line-clamp-2">
                    {project.description}
                  </p> */}
                  <div className="flex items-center gap-4 mt-3 pt-3 border-t border-zinc-100">
                    <span className="text-zinc-500 text-xs">{project.location}</span>
                    {/* Feel free to enable the duration of the project below. */}
                    {/* <span className="text-zinc-300">•</span>
                    <span className="text-zinc-500 text-xs">{project.duration}</span> */}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Full Screen Gallery - Opens directly on click */}
      {selectedProject && (
        <div className="fixed inset-0 z-50 bg-black">
          <MediaGallery
            media={selectedProject.media}
            projectTitle={selectedProject.title}
            onClose={closeGallery}
          />
        </div>
      )}

      {/* CTA Section - Dark */}
      <section className="py-16 lg:py-20 bg-zinc-900">
        <div className="max-w-4xl mx-auto px-4 lg:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-black text-white uppercase tracking-tight mb-6">
            Got a Project in Mind?
          </h2>
          <p className="text-zinc-400 mb-8 text-lg">
            Let's talk about what you need. Free estimates, straight answers.
          </p>
          <Link to="/contact">
            <Button className="bg-orange-500 hover:bg-orange-600 text-white font-black uppercase tracking-wide px-8 py-6 text-base rounded-none">
              Get a Free Quote
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ProjectsPage;
